import { uniqBy } from "lodash-es";

import { useTenantProfileV2 } from "@/common/hooks/entity/tenant/useTenantProfileV2";
import { EntityInputFormInfo } from "@/common/ts/entity";
import { apiClient } from "@/core/api/ApiClient";
import { VehicleDto } from "@/core/api/generated/v1";

import FormikComputedField from "../../Form/Formik/FormikComputedField";

export type AffiliationInfoDisplayVariant = "normal" | "compact";

interface Props {
  formInfo: EntityInputFormInfo;
  values: {
    vehicleId: Nil<string>;
    vehicle: Nil<VehicleDto>;
    departmentIds: Nil<string[]>;
    locationIds: Nil<string[]>;
  };
  onComputed: (computed: {
    vehicle: Nil<VehicleDto>;
    departmentIds: Nil<string[]>;
    locationIds: Nil<string[]>;
  }) => void;
}

/** Infers affiliation from vehicle based on form mode:
 * - Create: always infer
 * - Update: infer only when vehicle changes
 */
export default function InferEntityAffiliationFromVehicleFormikComputedField({
  formInfo,
  values,
  onComputed,
}: Props) {
  const tenantProfile = useTenantProfileV2();

  return (
    <FormikComputedField<
      typeof values,
      Pick<typeof values, "vehicle" | "departmentIds" | "locationIds"> | null
    >
      isEnabled={(params) =>
        tenantProfile.isInited &&
        (formInfo.isCreate ||
          (formInfo.isUpdate &&
            !params.isInitialRun &&
            params.values.vehicleId !== params.prevValues?.vehicleId)) &&
        (!(params.values.departmentIds || params.values.locationIds) ||
          !params.values.vehicle ||
          params.values.vehicle?.id !== params.values.vehicleId)
      }
      debouncedDeps={{
        deps: [values.vehicleId],
        wait: 100,
        options: { leading: false, trailing: true },
      }}
      compute={async (params) => {
        console.log(5, params);
        let vehicle2 = params.values.vehicle;
        if (params.values.vehicleId && params.values.vehicle?.id !== params.values.vehicleId) {
          const response = await apiClient.vehiclesApi.apiV1VehiclesVehicleIdGet({
            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            vehicleId: params.values.vehicleId!,
          });
          vehicle2 = response.data;
        }

        // skip if vehicle is from the different tenant (e.g. shared via data grants in T2T requests)
        const isFromSameTenant =
          !!vehicle2?.tenantId &&
          !!tenantProfile.profile?.id &&
          vehicle2?.tenantId === tenantProfile.profile?.id;
        if (!isFromSameTenant) {
          return null; // null means skip later
        }

        // add inferred deps/locs to the list and let caller decide when to reset them (e.g. on vehicle change)
        return {
          vehicle: vehicle2,
          departmentIds: uniqBy(
            [...(params.values.departmentIds || []), ...(vehicle2?.departmentIds || [])],
            (x) => x,
          ),
          locationIds: uniqBy(
            [...(params.values.locationIds || []), ...(vehicle2?.locationIds || [])],
            (x) => x,
          ),
        };
      }}
      onComputed={(computed) => {
        if (computed) {
          onComputed(computed);
        }
      }}
    />
  );
}
